/**
 * statusColors — zentrale Ampel-Farbwelt (grün/gelb/rot) für das Lager-Modul.
 * Eine Quelle der Wahrheit für „Wert → Status → Farbklassen", damit Bestand,
 * Durchlaufzeiten, Berichte etc. konsistent einfärben (CI-Blau bleibt neutral).
 *
 * Status-Keys: 'green' | 'yellow' | 'red' | 'neutral'
 */

/**
 * Bestands-Ampel: ausreichend (grün) / nachbestellen (gelb) / kritisch (rot).
 * @param {number} bestand   frei verfügbarer Bestand
 * @param {number} minbe     Meldebestand
 * @param {number} eisbe     Sicherheitsbestand
 */
export function getBestandStatus(bestand, minbe, eisbe) {
  const b = Number(bestand) || 0;
  const m = Number(minbe) || 0;
  const e = Number(eisbe) || 0;
  if (m > 0 && b >= m) return 'green';
  if (b > e) return 'yellow';
  return 'red';
}

/**
 * Lead-Time-Ampel: planmäßig (grün) / grenzwertig (gelb) / zu lang (rot),
 * relativ zur Zielzeit. <=80% Ziel = grün, <=120% = gelb, sonst rot.
 * @param {number} days     (geplante) Lieferzeit in Tagen
 * @param {number} target   Zielzeit in Tagen
 */
export function getLeadTimeStatus(days, target) {
  const d = Number(days) || 0;
  const t = Number(target) || 0;
  if (t <= 0) return 'neutral';
  if (d <= t * 0.8) return 'green';
  if (d <= t * 1.2) return 'yellow';
  return 'red';
}

/**
 * Liefert Tailwind-Klassen-Set für einen Status, theme-abhängig.
 * @param {'green'|'yellow'|'red'|'neutral'} status
 * @param {boolean} isDarkMode
 * @returns {{ wrap, val, tint, bar, badge, ring }}
 */
export function statusClasses(status, isDarkMode) {
  const M = {
    green: {
      wrap:  isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-600',
      val:   isDarkMode ? 'text-emerald-300' : 'text-emerald-700',
      tint:  isDarkMode ? 'from-emerald-500/10' : 'from-emerald-50',
      bar:   isDarkMode ? 'bg-emerald-500/70' : 'bg-emerald-500',
      badge: isDarkMode ? 'bg-emerald-500/20 text-emerald-300' : 'bg-emerald-100 text-emerald-700',
      ring:  isDarkMode ? 'ring-emerald-400/40' : 'ring-emerald-300',
    },
    yellow: {
      wrap:  isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-600',
      val:   isDarkMode ? 'text-amber-200' : 'text-amber-700',
      tint:  isDarkMode ? 'from-amber-500/10' : 'from-amber-50',
      bar:   isDarkMode ? 'bg-amber-500/70' : 'bg-amber-500',
      badge: isDarkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700',
      ring:  isDarkMode ? 'ring-amber-400/40' : 'ring-amber-300',
    },
    red: {
      wrap:  isDarkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-600',
      val:   isDarkMode ? 'text-rose-300' : 'text-rose-700',
      tint:  isDarkMode ? 'from-rose-500/10' : 'from-rose-50',
      bar:   isDarkMode ? 'bg-rose-500/70' : 'bg-rose-500',
      badge: isDarkMode ? 'bg-rose-500/20 text-rose-300' : 'bg-rose-100 text-rose-700',
      ring:  isDarkMode ? 'ring-rose-400/40' : 'ring-rose-300',
    },
    neutral: {
      wrap:  isDarkMode ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-500',
      val:   isDarkMode ? 'text-white/80' : 'text-slate-700',
      tint:  isDarkMode ? 'from-white/5' : 'from-slate-50',
      bar:   isDarkMode ? 'bg-blue-500/60' : 'bg-brand/60',
      badge: isDarkMode ? 'bg-white/10 text-white/60' : 'bg-slate-100 text-slate-600',
      ring:  isDarkMode ? 'ring-white/20' : 'ring-slate-300',
    },
  };
  return M[status] || M.neutral;
}
